import { directoryClient } from 'shared/domain/directory/directoryClient';
import type { StateCreator } from 'zustand';
import { createProjectChangeCheck } from '../moduleLifecycle/model';
import { areDirectoryIdsEqual } from 'shared/domain/directory/directoryIds';
import { logError } from 'setup/logger/logError';
import { NavigationSlice, NavigationSliceState, DirectoryRoot } from './types';
import type { DocumentationStoreSlices } from '../types';

export * from './types';

const INITIAL_NAVIGATION_STATE: NavigationSliceState = {
  currentDirectory: '',
  breadcrumbs: [],
  isLoadingBreadcrumbs: false,
  previousDirectory: null,
  currentDirectoryPath: [],
  isLoadingCurrentDirectoryPath: false,
  // Saved search state for "return to search results"
  savedSearchPhrase: null,
  savedSearchDirectory: null,
  // Breadcrumb hover bar state
  breadcrumbCache: new Map(),
  hoveredDirectoryId: null,
  hoveredBreadcrumbs: [],
  isLoadingHoveredBreadcrumbs: false,
  hoveredRowTop: null,
  hoveredCellRight: null,
  urlFileOpenPending: false,
};

type NavigationSliceCreator = StateCreator<
  DocumentationStoreSlices,
  [],
  [],
  NavigationSlice
>;

function buildOptimisticBreadcrumbs(
  breadcrumbs: NavigationSliceState['breadcrumbs'],
  directoryId: string,
  directoryName?: string
): NavigationSliceState['breadcrumbs'] {
  if (!directoryId) {
    return [];
  }

  const existingIndex = breadcrumbs.findIndex((item) => item._id === directoryId);
  if (existingIndex >= 0) {
    return breadcrumbs.slice(0, existingIndex + 1);
  }

  if (!directoryName) {
    return breadcrumbs;
  }

  return [...breadcrumbs, { _id: directoryId, name: directoryName }];
}

export const createNavigationSlice: NavigationSliceCreator = (set, get) => ({
  ...INITIAL_NAVIGATION_STATE,

  setCurrentDirectory: (directoryId: string): void => {
    const { currentDirectory, breadcrumbs } = get();
    set({
      previousDirectory: currentDirectory,
      currentDirectory: directoryId,
      // Root has no breadcrumbs of its own - clear any stale trail left over
      // from a previous directory instead of leaving it to be corrected by a
      // later fetchBreadcrumbs call that may never come for the root case.
      breadcrumbs: directoryId ? breadcrumbs : [],
    });
  },

  navigateToDirectory: async (
    directoryId: string,
    directoryName?: string
  ): Promise<void> => {
    const state = get();
    const { 
      currentDirectory, 
      breadcrumbs,
      setSearchPhrase, 
      fetchDirectoryContent,
      clearUploadedDirectoryHighlight,
      resetOptimisticState,
    } = state;

    if (directoryId === currentDirectory) {
      return;
    }

    // Clear optimistic state from previous directory before navigating.
    resetOptimisticState();

    set({
      previousDirectory: currentDirectory,
      currentDirectory: directoryId,
      breadcrumbs: buildOptimisticBreadcrumbs(
        breadcrumbs,
        directoryId,
        directoryName
      ),
      isLoadingBreadcrumbs: true,
      isLoadingCurrentDirectoryPath: true,
    });

    setSearchPhrase('');
    clearUploadedDirectoryHighlight(directoryId);

    const breadcrumbsPromise = get().fetchBreadcrumbs(directoryId);

    // Fetch fresh content
    const contentPromise = fetchDirectoryContent(directoryId);

    await Promise.all([breadcrumbsPromise, contentPromise]);
  },

  navigateToDirectoryFromSearch: async (
    directoryId: string,
    directoryName?: string
  ): Promise<void> => {
    const state = get();
    const { 
      currentDirectory, 
      searchPhrase,
      navigateToDirectory,
    } = state;

    // Save current search state before navigating
    if (searchPhrase) {
      set({
        savedSearchPhrase: searchPhrase,
        savedSearchDirectory: currentDirectory,
      });
    }

    await navigateToDirectory(directoryId, directoryName);
  },

  returnToSearchResults: (): void => {
    const state = get();
    const { 
      savedSearchPhrase,
      savedSearchDirectory,
      setSearchPhrase,
      navigateToDirectory,
    } = state;

    if (!savedSearchPhrase) {
      return;
    }

    // Clear saved state
    set({
      savedSearchPhrase: null,
      savedSearchDirectory: null,
    });

    // Navigate back to original directory and restore search
    if (savedSearchDirectory !== null) {
      void navigateToDirectory(savedSearchDirectory).then(() => {
        setSearchPhrase(savedSearchPhrase);
      });
    } else {
      setSearchPhrase(savedSearchPhrase);
    }
  },

  navigateUp: (): void => {
    const { breadcrumbs, navigateToDirectory } = get();
    
    if (breadcrumbs.length < 2) {
      void navigateToDirectory('');
      return;
    }

    const parentDirectory = breadcrumbs[breadcrumbs.length - 2];
    if (parentDirectory) {
      void navigateToDirectory(parentDirectory._id, parentDirectory.name);
    }
  },

  navigateToRoot: (): void => {
    const { navigateToDirectory } = get();
    void navigateToDirectory('');
  },

  // One `findRoots` request fills the breadcrumbs and the directory path (they
  // are the same ancestor list).
  fetchBreadcrumbs: async (directoryId: string): Promise<void> => {
    set({ isLoadingBreadcrumbs: true, isLoadingCurrentDirectoryPath: true });
    const hasProjectChanged = createProjectChangeCheck(get);
    // A newer navigation (or project) owns the breadcrumbs - drop this result.
    const isStale = (): boolean =>
      hasProjectChanged() || !areDirectoryIdsEqual(get().currentDirectory, directoryId);

    try {
      const breadcrumbs = await directoryClient.getBreadcrumbs(directoryId);
      if (isStale()) {
        return;
      }
      set({
        breadcrumbs,
        isLoadingBreadcrumbs: false,
        currentDirectoryPath: breadcrumbs,
        isLoadingCurrentDirectoryPath: false,
      });
    } catch (error) {
      logError('Error fetching breadcrumbs', error);
      if (!isStale()) {
        set({ isLoadingBreadcrumbs: false, isLoadingCurrentDirectoryPath: false });
      }
    }
  },

  setCurrentDirectoryPath: (path: DirectoryRoot[]): void => {
    set({ currentDirectoryPath: path, isLoadingCurrentDirectoryPath: false });
  },

  resetNavigationState: (): void => {
    set(INITIAL_NAVIGATION_STATE);
  },

  setHoveredDirectory: (directoryId: string | null, rowTop?: number | null, cellRight?: number | null): void => {
    if (directoryId === null) {
      set({ hoveredDirectoryId: null, hoveredRowTop: null, hoveredCellRight: null });
      return;
    }
    set({ hoveredDirectoryId: directoryId, hoveredRowTop: rowTop ?? null, hoveredCellRight: cellRight ?? null });
  },

  fetchHoveredBreadcrumbs: async (directoryId: string): Promise<void> => {
    if (!directoryId) {
      set({ hoveredBreadcrumbs: [], isLoadingHoveredBreadcrumbs: false });
      return;
    }

    const { breadcrumbCache, hoveredDirectoryId } = get();

    // Check cache first
    const cached = breadcrumbCache.get(directoryId);
    if (cached) {
      if (hoveredDirectoryId === directoryId) {
        set({ hoveredBreadcrumbs: cached, isLoadingHoveredBreadcrumbs: false });
      }
      return;
    }

    set({ isLoadingHoveredBreadcrumbs: true });

    try {
      const breadcrumbs = await directoryClient.getBreadcrumbs(directoryId);
      
      // Update cache
      const newCache = new Map(get().breadcrumbCache);
      newCache.set(directoryId, breadcrumbs);
      
      // Only update visible breadcrumbs if still hovering the same directory
      if (get().hoveredDirectoryId === directoryId) {
        set({
          breadcrumbCache: newCache,
          hoveredBreadcrumbs: breadcrumbs,
          isLoadingHoveredBreadcrumbs: false,
        });
      } else {
        // Just update cache
        set({ breadcrumbCache: newCache, isLoadingHoveredBreadcrumbs: false });
      }
    } catch (error) {
      logError('Error fetching hovered breadcrumbs', error);
      set({ isLoadingHoveredBreadcrumbs: false });
    }
  },

  clearHoveredBreadcrumbs: (): void => {
    set({
      hoveredDirectoryId: null,
      hoveredBreadcrumbs: [],
      isLoadingHoveredBreadcrumbs: false,
      hoveredRowTop: null,
      hoveredCellRight: null,
    });
  },

  setUrlFileOpenPending: (pending: boolean): void => {
    set({ urlFileOpenPending: pending });
  },
});
